/** Step 4/4 — good/avoid copy examples per AI-09 §C1 (≤10 each, §G2). */
import { useTranslation } from "react-i18next";

import {
  VOICE_EXAMPLE_CATEGORIES,
  type BrandVoiceExample,
  type VoiceExampleCategory,
} from "../../../../lib/brandKits";
import type { StepProps } from "./ToneStep";

const MAX_EXAMPLES = 10;

export function ExamplesStep({ draft, update }: StepProps): JSX.Element {
  const { t } = useTranslation();

  function listOf(kind: "good" | "avoid"): BrandVoiceExample[] {
    return draft.examples?.[kind] ?? [];
  }

  function setList(kind: "good" | "avoid", list: BrandVoiceExample[]): void {
    update({ examples: { ...draft.examples, [kind]: list } });
  }

  function section(kind: "good" | "avoid"): JSX.Element {
    const list = listOf(kind);
    return (
      <div>
        <p className="mb-2 text-sm font-medium text-neutral-800">
          {t(`brandVoice.examples.${kind}_title`)}{" "}
          <span className="font-normal text-neutral-400">
            ({list.length}/{MAX_EXAMPLES})
          </span>
        </p>
        <ul className="space-y-2">
          {list.map((example, i) => (
            <li key={`${kind}-${i}`} className="flex items-center gap-2">
              <select
                aria-label={t("brandVoice.examples.category_label")}
                value={example.category}
                onChange={(e) =>
                  setList(
                    kind,
                    list.map((ex, j) =>
                      j === i ? { ...ex, category: e.target.value as VoiceExampleCategory } : ex,
                    ),
                  )
                }
                className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
              >
                {VOICE_EXAMPLE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(`brandVoice.examples.categories.${category}`)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                aria-label={t(`brandVoice.examples.${kind}_text_label`)}
                value={example.text}
                maxLength={300}
                onChange={(e) =>
                  setList(
                    kind,
                    list.map((ex, j) => (j === i ? { ...ex, text: e.target.value } : ex)),
                  )
                }
                className="flex-1 rounded-md border border-neutral-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button
                type="button"
                aria-label={t("brandVoice.examples.remove")}
                className="text-neutral-400 hover:text-neutral-700"
                onClick={() => setList(kind, list.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          disabled={list.length >= MAX_EXAMPLES}
          onClick={() => setList(kind, [...list, { category: "subject_line", text: "" }])}
          className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:text-neutral-300"
        >
          + {t("brandVoice.examples.add")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {section("good")}
      {section("avoid")}
    </div>
  );
}
